"use client";

import React, { useState, useCallback } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { ResponsivePopover } from "@/components/ui/responsive-popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Calendar as CalendarIcon } from "lucide-react";
import { format, parse } from "date-fns";
import { cn } from "@/lib/utils";

export interface CalendarDateEditProps {
  value: string | null | undefined;
  editValue: string;
  setEditValue: (value: string) => void;
  onSave: (rowId: string, fieldId: string, newValue: string) => Promise<void>;
  onCancel: () => void;
  rowId: string;
  fieldId: string;
  className?: string;
}

/**
 * Calendar-based date editor for inline editing
 * Provides a visual calendar picker with manual date input fallback
 */
export function CalendarDateEdit({
  value,
  editValue,
  setEditValue,
  onSave,
  onCancel,
  rowId,
  fieldId,
  className,
}: CalendarDateEditProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputError, setInputError] = useState<string | null>(null);

  // Parse the current date value
  const currentDate = editValue
    ? parse(editValue, "yyyy-MM-dd", new Date())
    : value
      ? parse(String(value), "yyyy-MM-dd", new Date())
      : new Date();

  // Handle calendar date selection
  const handleDateSelect = useCallback(
    (date: Date | undefined) => {
      if (date) {
        const formattedDate = format(date, "yyyy-MM-dd");
        setEditValue(formattedDate);
        setInputError(null);
        setIsOpen(false);
        // Pass the new value directly to onSave to avoid stale closures
        setTimeout(() => {
          onSave(rowId, fieldId, formattedDate).catch((error) => {
            console.error("Error saving date:", error);
          });
        }, 150);
      }
    },
    [setEditValue, onSave, rowId, fieldId]
  );

  // Handle manual date input
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      setEditValue(inputValue);

      // Validate date format
      if (inputValue) {
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(inputValue)) {
          setInputError("Format: YYYY-MM-DD");
        } else {
          const parsedDate = parse(inputValue, "yyyy-MM-dd", new Date());
          if (isNaN(parsedDate.getTime())) {
            setInputError("Invalid date");
          } else {
            setInputError(null);
          }
        }
      } else {
        setInputError(null);
      }
    },
    [setEditValue]
  );

  // Handle clear button
  const handleClear = useCallback(() => {
    setEditValue("");
    setInputError(null);
  }, [setEditValue]);

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (!inputError && editValue) {
          onSave(rowId, fieldId, editValue).catch((error) => {
            console.error("Error saving date:", error);
          });
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
    },
    [editValue, inputError, onSave, onCancel, rowId, fieldId]
  );

  const calendarPanel = (
    <div className="p-2">
      <DayPicker
        mode="single"
        selected={
          editValue ? parse(editValue, "yyyy-MM-dd", new Date()) : currentDate
        }
        onSelect={handleDateSelect}
        disabled={(date: Date) => {
          // Disable dates in the future if needed
          // return date > new Date();
          return false;
        }}
        initialFocus
        classNames={{
          months: "flex flex-col space-y-0",
          month: "space-y-0",
          caption: "flex justify-center pt-1 pb-2 relative items-center text-sm font-medium",
          caption_label: "text-xs",
          head_row: "flex",
          head_cell: "text-gray-500 rounded-md w-8 font-normal text-xs",
          row: "flex w-full mt-1",
          cell: "h-8 w-8 text-center text-xs p-0 relative",
          day: "h-8 w-8 p-0 font-normal text-xs rounded hover:bg-gray-100 dark:hover:bg-gray-800",
          day_selected: "bg-blue-500 text-white hover:bg-blue-600",
          day_today: "bg-gray-100 dark:bg-gray-800 font-semibold",
          day_outside: "text-gray-300",
          day_disabled: "text-gray-300",
          day_range_middle: "aria-selected:bg-gray-100 dark:aria-selected:bg-gray-800",
          day_hidden: "invisible",
        }}
      />
    </div>
  );

  return (
    <div className={cn("relative z-[10000] w-full pointer-events-auto", className)}>
      {/* The desktop branch of ResponsivePopover is a Mantine popover portalled
          to document.body at z-index 1100 — it escapes the table's overflow and
          outranks every z token (max z-drawer 80) plus Mantine modals (200), so
          the calendar needs no z class of its own. Mobile gets a drawer. */}
      <ResponsivePopover forcePopover
        open={isOpen}
        onOpenChange={setIsOpen}
        content={calendarPanel}
        title="Select date"
        align="start"
        popoverSide="bottom"
        offset={4}
        className="w-auto p-0 shadow-lg border border-gray-200 dark:border-gray-700"
        showCloseButton={false}
        asChild
      >
        <div
          className="flex items-center gap-1 w-full pointer-events-auto cursor-pointer"
          // Controlled popover: the trigger owns opening (the Mantine target
          // injects no toggle when `open` is supplied). Value-form update to
          // match the calendar Button's own toggle.
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="flex-1 relative">
            <Input
              type="text"
              value={editValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="YYYY-MM-DD"
              className={cn(
                "h-8 text-sm pr-8",
                inputError && "border-red-500 focus:border-red-500"
              )}
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
            {editValue && (
              <button type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-400 transition-colors"
                title="Clear date"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
            title="Open calendar"
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </div>
      </ResponsivePopover>

      {inputError && (
        <div className="text-xs text-red-500 mt-1">{inputError}</div>
      )}
    </div>
  );
}
